import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../FirebaseConfig";

const PINCODE_API =
  "https://us-central1-tghc-web.cloudfunctions.net/checkPincodeServiceability";

const STORAGE_KEY = "pincode_check_result"; // { pin, res }
const QUOTA_KEY = "pincode_check_quota";    // { date, count }
const DAILY_LIMIT = 5;

const validPin = (p) => /^[1-9][0-9]{5}$/.test(p || "");
const todayStr = () => new Date().toISOString().slice(0, 10);

const safeReadLocal = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeWriteLocal = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

const readQuota = () => {
  const obj = safeReadLocal(QUOTA_KEY) || {};
  const date = obj.date === todayStr() ? obj.date : todayStr();
  const count = obj.date === todayStr() ? Number(obj.count) || 0 : 0;
  return { date, count };
};

const writeQuota = (q) => {
  safeWriteLocal(QUOTA_KEY, q);
};

const PincodeETA = ({ productId }) => {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [user, setUser] = useState(auth.currentUser || null);
  const [quota, setQuota] = useState(readQuota());
  const [storedPin, setStoredPin] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = useMemo(
    () => encodeURIComponent(location.pathname + location.search),
    [location]
  );

  // Sync auth state
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setUser(u));
    return () => unsub && unsub();
  }, []);

  // Load stored pin/result on mount
  useEffect(() => {
    const stored = safeReadLocal(STORAGE_KEY);
    if (stored?.pin && stored?.res) {
      setPin(stored.pin);
      setStoredPin(stored.pin);
      setRes(stored.res);
    }
    setQuota(readQuota());
  }, []);

  const quotaExceeded = quota.count >= DAILY_LIMIT;
  const checksLeft = Math.max(0, DAILY_LIMIT - quota.count);
  const isValidPin = validPin(pin);

  const bumpQuota = () => {
    const current = readQuota();
    const next = { date: todayStr(), count: (current.count || 0) + 1 };
    writeQuota(next);
    setQuota(next);
  };

  const handleCheck = async () => {
    setError("");

    if (!user) {
      navigate(`/login?redirect=${redirectTo}`);
      return;
    }

    if (!isValidPin) {
      setError("Enter a valid 6-digit PIN code.");
      return;
    }

    if (storedPin && pin === storedPin && res) {
      return;
    }

    if (quotaExceeded) {
      setError("Daily limit reached. Please try again tomorrow.");
      return;
    }

    setLoading(true);
    try {
      bumpQuota();

      const token = await user.getIdToken(true);
      const qs = new URLSearchParams({
        pincode: pin,
        ...(productId ? { productId } : {}),
      });

      const r = await fetch(`${PINCODE_API}?${qs.toString()}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });

      if (r.status === 401 || r.status === 403) {
        throw new Error("Auth failed");
      }
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }

      const data = await r.json(); // { serviceable, state, city }
      const serviceable = !!data?.serviceable;
      const state = data?.state || "";
      const city = data?.city || "";

      const result = {
        serviceable,
        etaText: serviceable ? `${city ? `${city}, ` : ""}${state}` : undefined,
        message: serviceable
          ? undefined
          : "Currently not serviceable for this PIN.",
      };

      setRes(result);
      setStoredPin(pin);
      safeWriteLocal(STORAGE_KEY, { pin, res: result });
    } catch (e) {
      console.error(e);
      const msg = String(e?.message || "");
      setError(
        msg.includes("Auth failed")
          ? "Authorization failed. Please log in again and try."
          : "Unable to fetch delivery details right now."
      );
    } finally {
      setLoading(false);
    }
  };

  const loginCTA = !user;

  return (
    <div className="mt-5">
      <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-4 sm:px-5 sm:py-4 shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M3 7a2 2 0 0 1 2-2h9v7h-3a3 3 0 0 0-2.83 2H8a3 3 0 0 0-2.83 2H4a1 1 0 0 1-1-1zm11 0h2.382a2 2 0 0 1 1.789 1.106L20.618 11H21a1 1 0 0 1 1 1v5h-1.17a3.001 3.001 0 0 0-4.66 0H10.83a3.001 3.001 0 0 0-4.66 0H5a3 3 0 0 0 5.83 0h4.34a3 3 0 0 0 5.83 0H22v-3h-2.382a2 2 0 0 1-1.789-1.106L16.382 9H14zM8 17a1 1 0 1 0-2 0a1 1 0 0 0 2 0m10 0a1 1 0 1 0-2 0a1 1 0 0 0 2 0"
                />
              </svg>
            </span>
            <div>
              <div className="text-sm font-semibold text-gray-900">
                Delivery to your area?
              </div>
              <div className="text-xs text-gray-600">
                Check if we can deliver this product to your PIN.
              </div>
            </div>
          </div>

          <div className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-white border border-amber-200 text-gray-700">
            {checksLeft} / {DAILY_LIMIT} left today
          </div>
        </div>

        {/* Input + Button */}
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="tel"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={(e) =>
              setPin(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="Enter 6-digit PIN"
            className="w-full sm:w-40 px-3 py-2 border border-amber-200 rounded-md text-sm focus:ring-2 focus:ring-amber-300 focus:outline-none bg-white"
          />

          <button
            onClick={handleCheck}
            disabled={loading || quotaExceeded || !isValidPin}
            className={[
              "inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-semibold transition",
              loading || !isValidPin || quotaExceeded
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-amber-600 text-white hover:bg-amber-700 shadow-sm hover:shadow-md",
            ].join(" ")}
          >
            {loginCTA
              ? "Login to check"
              : loading
              ? "Checking..."
              : quotaExceeded
              ? "Limit reached"
              : "Check"}
          </button>

          <div className="sm:hidden text-[11px] text-gray-600">
            {checksLeft} / {DAILY_LIMIT} left today
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-100">
            <span className="h-2 w-2 rounded-full bg-red-500" />
            <span>{error}</span>
          </div>
        )}

        {res && !error && (
          <div className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-2">
            {res.serviceable ? (
              <>
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-semibold text-emerald-800">
                  Delivery available
                </span>
                {res.etaText && (
                  <span className="text-xs text-gray-800">{res.etaText}</span>
                )}
                {storedPin && pin === storedPin && (
                  <span className="text-[11px] text-gray-600">
                    (Showing saved result)
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="h-2 w-2 rounded-full bg-red-500" />
                <span className="text-xs font-semibold text-red-800">
                  Not serviceable
                </span>
                {res.message && (
                  <span className="text-xs text-gray-800">{res.message}</span>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PincodeETA;
